import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  FiEye,
  FiCheckCircle,
  FiLoader,
  FiRefreshCw,
  FiInbox,
} from 'react-icons/fi';
import dayjs from 'dayjs';

// --- Your Custom Components ---
import { Button } from '../../ComponnentsUI/common/Button';
import { Table } from '../../ComponnentsUI/common/Table';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../ComponnentsUI/common/ActionMenu';

// --- Your API Hooks & Utils ---
import {
  useGetPacsStudiesPageable,
  useGetPacsStudyWithInstances,
} from '../../../helpers/api/pacs/pacs';

// --- Existing Modals & Components ---
import PacsStudyDetailsModal from '../../Components/PacsStudyDetailsModal';
import { NewCaseWizard } from '../../ComponnentsUI/NewCaseWizard/NewCaseWizard';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

// --- Helper Functions ---
const formatDate = (dateString?: string) => {
  if (!dateString) {
    return 'N/A';
  }
  const date = dayjs(
    dateString.includes('T')
      ? dateString
      : dateString.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
  );
  return date.isValid() ? date.format('MMMM DD, YYYY') : 'Invalid Date';
};

const formatDateTime = (dateString?: string, timeString?: string) => {
  if (!dateString) {
    return 'N/A';
  }

  let formattedDate = dateString;
  if (!dateString.includes('T')) {
    formattedDate = dateString.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
  }

  if (timeString && timeString.length >= 6) {
    const time = timeString.replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2:$3');
    const date = dayjs(`${formattedDate} ${time}`);
    return date.isValid()
      ? date.format('MMM DD, YYYY HH:mm:ss')
      : 'Invalid Date';
  }

  const date = dayjs(formattedDate);
  return date.isValid() ? date.format('MMM DD, YYYY') : 'Invalid Date';
};

const formatReceivedAt = (dateTimeString?: string) => {
  if (!dateTimeString) {
    return 'N/A';
  }
  const date = dayjs(dateTimeString);
  return date.isValid() ? date.format('MMM DD, YYYY HH:mm:ss') : 'Invalid Date';
};

const parsePatientName = (patientName?: string) => {
  if (!patientName) {
    return 'N/A';
  }
  return patientName.replace(/\^/g, ' ').trim() || 'N/A';
};

// --- Interfaces for Type Safety ---
interface PacsStudy {
  id: string;
  patientId: string | null;
  patientName: string;
  patientBirthDate: string | null;
  studyDate: string;
  studyTime: string | null;
  studyInstanceUID: string;
  sourceAeTitle: string;
  receivedAt: string;
  aeType: string | null;
  numberOfInstances: number;
  syncedInstanceCount: number;
  unsyncedInstanceCount: number;
  fullySync: boolean;
}

// --- Main Page Component ---
const StudyInbox = () => {
  const { t } = useTranslation('ExamManagement');
  useDocumentTitle(t('studyInbox.documentTitle'));

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isPacsModalOpen, setIsPacsModalOpen] = useState(false);
  const [selectedPacsStudy, setSelectedPacsStudy] = useState<PacsStudy | null>(
    null
  );
  const [pacsStudyDetails, setPacsStudyDetails] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const {
    mutate: fetchPacsStudies,
    data: pacsStudiesData,
    isPending: isLoadingPacsStudies,
  } = useGetPacsStudiesPageable();
  const { mutate: fetchPacsStudyDetails } = useGetPacsStudyWithInstances();

  useEffect(() => {
    fetchPacsStudies({
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortBy: 'receivedAt',
      sortDirection: 'DESC',
    });
  }, [pagination.pageIndex, pagination.pageSize, fetchPacsStudies]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchPacsStudies({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortBy: 'receivedAt',
        sortDirection: 'DESC',
      });
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(intervalId);
  }, [
    autoRefresh,
    pagination.pageIndex,
    pagination.pageSize,
    fetchPacsStudies,
  ]);

  const handleViewPacsStudyDetails = useCallback(
    (study: PacsStudy) => {
      setSelectedPacsStudy(study);
      fetchPacsStudyDetails(study.id, {
        onSuccess: (response) => {
          setPacsStudyDetails(response);
          setIsPacsModalOpen(true);
        },
      });
    },
    [fetchPacsStudyDetails]
  );

  const handleRefresh = () => {
    fetchPacsStudies({
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortBy: 'receivedAt',
      sortDirection: 'DESC',
    });
  };

  const columns = useMemo<ColumnDef<PacsStudy>[]>(
    () => [
      {
        accessorKey: 'patientId',
        header: t('table.columns.patientId'),
        cell: (info) => (
          <div className="font-medium text-title-page-color-100">
            {String(info.getValue() || t('common.notAvailable'))}
          </div>
        ),
      },
      {
        accessorKey: 'patientName',
        header: t('table.columns.patientName'),
        cell: (info) => (
          <div className="font-medium text-title-page-color-100">
            {parsePatientName(String(info.getValue() || ''))}
          </div>
        ),
      },
      {
        accessorKey: 'patientBirthDate',
        header: t('table.columns.birthDate'),
        cell: (info) => formatDate(info.getValue<string>()),
      },
      {
        accessorKey: 'studyDate',
        header: t('table.columns.studyDate'),
        cell: ({ row }) => {
          const study = row.original;
          return formatDateTime(study.studyDate, study.studyTime);
        },
      },
      {
        accessorKey: 'receivedAt',
        header: t('table.columns.receivedAt'),
        cell: (info) => formatReceivedAt(info.getValue<string>()),
      },
      {
        accessorKey: 'sourceAeTitle',
        header: t('table.columns.sourceAeTitle'),
        cell: (info) => (
          <span className="text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-2 py-1 rounded-full">
            {String(info.getValue() || t('common.notAvailable'))}
          </span>
        ),
      },
      {
        accessorKey: 'aeType',
        header: t('table.columns.aeType'),
        cell: (info) => {
          const value = info.getValue<string>();
          return value ? (
            <span className="text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded-full">
              {value}
            </span>
          ) : (
            <span className="text-xs text-gray-500">{t('common.notAvailable')}</span>
          );
        },
      },
      {
        accessorKey: 'numberOfInstances',
        header: t('table.columns.totalInstances'),
        cell: ({ row }) => {
          const study = row.original;
          const syncedCount = study.syncedInstanceCount || 0;
          const unsyncedCount = study.unsyncedInstanceCount || 0;
          return (
            <div className="space-y-1">
              <div className="text-sm font-medium">
                {t('studyInbox.instancesTotal', {
                  count: study.numberOfInstances,
                })}
              </div>
              <div className="flex gap-2">
                {syncedCount > 0 && (
                  <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                    {t('studyInbox.instancesSynced', { count: syncedCount })}
                  </span>
                )}
                {unsyncedCount > 0 && (
                  <span className="text-xs font-medium bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                    {t('studyInbox.instancesUnsynced', {
                      count: unsyncedCount,
                    })}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'fullySync',
        header: t('table.columns.syncStatus'),
        cell: ({ row }) => {
          const study = row.original;
          return study.fullySync ? (
            <span className="inline-flex items-center text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded-full">
              <FiCheckCircle className="w-3 h-3 mr-1" />
              {t('sync.fullySynced')}
            </span>
          ) : (
            <span className="inline-flex items-center text-xs font-medium bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
              <FiLoader className="w-3 h-3 mr-1" />
              {t('studyInbox.unsyncedStatus', {
                count: study.unsyncedInstanceCount,
              })}
            </span>
          );
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <ActionMenu>
            <ActionMenuItem
              icon={FiEye}
              label={t('studyInbox.viewDetails')}
              onClick={() => handleViewPacsStudyDetails(row.original)}
              className=""
              disabled={false}
              onClose={() => {}}
            />
          </ActionMenu>
        ),
        size: 40,
      },
    ],
    [t, handleViewPacsStudyDetails]
  );

  const table = useReactTable({
    data: pacsStudiesData?.content ?? [],
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: pacsStudiesData?.totalElements ?? 0,
  });

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-6">
      <div className="max-w-full mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FiInbox
                  size={24}
                  className="text-blue-600 dark:text-blue-400"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
                  {t('studyInbox.pageTitle')}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('studyInbox.pageSubtitle')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-card-color-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="auto-refresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="auto-refresh"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none"
              >
                {t('studyInbox.autoRefresh')}
              </label>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleRefresh}
              disabled={isLoadingPacsStudies || autoRefresh}
            >
              <FiRefreshCw
                className={`mr-2 h-4 w-4 ${isLoadingPacsStudies || autoRefresh ? 'animate-spin' : ''}`}
              />
              {t('studyInbox.refresh')}
            </Button>
            {/* <NewCaseWizard /> */}
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <FiInbox
              className="text-blue-600 dark:text-blue-400 mt-0.5"
              size={18}
            />
            <div className="flex-1">
              <p className="text-sm text-blue-900 dark:text-blue-300">
                <Trans
                  i18nKey="studyInbox.banner"
                  ns="ExamManagement"
                  components={{ strong: <strong /> }}
                />
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card-color-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-title-page-color-100 dark:text-white flex items-center gap-2">
                <FiRefreshCw className="text-blue-600" size={18} />
                {t('studyInbox.receivedStudies')}
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('studyInbox.totalStudies', {
                  count: pacsStudiesData?.totalElements || 0,
                })}
              </span>
            </div>
          </div>

          <Table
            table={table}
            isLoading={isLoadingPacsStudies}
            columnsLength={table.getAllColumns().length}
          />
        </div>
      </div>

      <PacsStudyDetailsModal
        isModalOpen={isPacsModalOpen}
        setIsModalOpen={setIsPacsModalOpen}
        handleCancel={() => setIsPacsModalOpen(false)}
        selectedPacsStudy={selectedPacsStudy}
        pacsStudyDetails={pacsStudyDetails}
        onSyncSuccess={() => {
          fetchPacsStudies({
            page: pagination.pageIndex,
            size: pagination.pageSize,
            sortBy: 'receivedAt',
            sortDirection: 'DESC',
          });
        }}
        onRefetchStudyDetails={() => {
          if (selectedPacsStudy) {
            handleViewPacsStudyDetails(selectedPacsStudy);
          }
        }}
      />
    </div>
  );
};

export default StudyInbox;
